import { useEffect, useState, useCallback } from 'react';
import { Table, Typography, DatePicker, Button, Alert } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const pesos = (n) => `$ ${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = { fontVariantNumeric: 'tabular-nums' };

// ReporteTransferencias: cuánto entró a cada cuenta, y por lo tanto a cada
// tesorero, en un período. Lo aprobado ya está conciliado; lo que está en
// revisión todavía no se confirmó contra el banco.
export const ReporteTransferencias = () => {
  const { user } = useAuth();
  const esAdmin = user?.id_rol === 1;
  const [rango, setRango] = useState([dayjs().startOf('month'), dayjs()]);
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.admin.reporteTransferencias(rango[0].format('YYYY-MM-DD'), rango[1].format('YYYY-MM-DD'));
      if (res.status === 'success') setFilas(res.data?.filas || []);
    } finally {
      setLoading(false);
    }
  }, [rango]);

  useEffect(() => { cargar(); }, [cargar]);

  const totales = filas.reduce((t, f) => ({
    aprobadas: t.aprobadas + f.aprobadas, total_aprobado: t.total_aprobado + f.total_aprobado,
    en_revision: t.en_revision + f.en_revision, total_en_revision: t.total_en_revision + f.total_en_revision,
  }), { aprobadas: 0, total_aprobado: 0, en_revision: 0, total_en_revision: 0 });

  const sinCuenta = filas.find((f) => !f.id_cuenta);

  const columns = [
    {
      title: 'Cuenta', key: 'cuenta',
      render: (_, f) => (f.id_cuenta
        ? <Text strong>{f.cuenta}</Text>
        : <Text type="secondary">Sin cuenta indicada</Text>),
    },
    ...(esAdmin ? [{
      title: 'Tesorero', key: 'tesorero', width: 180,
      render: (_, f) => (f.id_cuenta ? (f.tesorero || <Text type="secondary">De la tienda</Text>) : null),
    }] : []),
    {
      title: 'Aprobadas', key: 'aprobadas', align: 'right', width: 170,
      render: (_, f) => (
        <div style={num}>
          <div style={{ fontWeight: 600 }}>{pesos(f.total_aprobado)}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{f.aprobadas} transferencia{f.aprobadas === 1 ? '' : 's'}</Text>
        </div>
      ),
    },
    {
      title: 'En revisión', key: 'revision', align: 'right', width: 170,
      render: (_, f) => (
        <div style={num}>
          <div>{pesos(f.total_en_revision)}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{f.en_revision} transferencia{f.en_revision === 1 ? '' : 's'}</Text>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Transferencias</Text>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <RangePicker
            value={rango}
            onChange={(v) => v && setRango(v)}
            format="DD/MM/YYYY"
            allowClear={false}
          />
          <Button icon={<ReloadOutlined />} onClick={cargar}>Actualizar</Button>
        </div>
      </div>
      <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 20 }}>
        {esAdmin
          ? 'Lo que entró a cada cuenta en el período. Aprobadas ya está conciliado; en revisión todavía no se confirmó contra el banco.'
          : 'Lo que entró a tus cuentas en el período. Aprobadas ya está conciliado; en revisión todavía no se confirmó contra el banco.'}
      </Text>

      {esAdmin && sinCuenta && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${sinCuenta.aprobadas + sinCuenta.en_revision} transferencia${sinCuenta.aprobadas + sinCuenta.en_revision === 1 ? '' : 's'} sin cuenta indicada`}
          description="Son anteriores a que el cliente eligiera la cuenta, o nadie la asignó. Se corrigen desde Cobros, en el detalle de cada pago."
        />
      )}

      <Table
        columns={columns}
        dataSource={filas}
        rowKey={(f) => f.id_cuenta || 'sin-cuenta'}
        loading={loading}
        pagination={false}
        size="middle"
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 8 }}
        locale={{ emptyText: 'No entraron transferencias en este período.' }}
        summary={() => filas.length > 1 && (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={esAdmin ? 2 : 1}><Text strong>Total</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <Text strong style={num}>{pesos(totales.total_aprobado)}</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              <Text style={num}>{pesos(totales.total_en_revision)}</Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};
